import React, { useState, useEffect } from 'react';
import { CheckCircle, X, Loader2 } from 'lucide-react';

/**
 * NPIEntryStep Component
 * 
 * Step 1 of Physician ID Proofing: NPI Number Entry/Confirmation
 * 
 * Features:
 * - Pre-populated NPI display (from invitation)
 * - Real-time Luhn validation
 * - Input formatting (numeric only, 10 digits max)
 * - Validation feedback with specific error messages
 * - Loading states
 * - Keyboard navigation support
 * - Full accessibility compliance
 */

interface NPIEntryStepProps {
  currentNPI: string;
  onNext: () => void;
  onBack: () => void;
  onNPIUpdate: (npi: string) => void;
  isLoading: boolean;
}

// NPI Format Validation (Frontend UX Only - Backend handles Luhn validation)
function validateNPI(npi: string): { isValid: boolean; error?: string } {
  if (!npi) {
    return { isValid: false, error: 'NPI is required' };
  }

  // Trim whitespace
  const cleanNPI = npi.trim();

  // Check if contains only numeric characters
  if (!/^\d+$/.test(cleanNPI)) {
    return { isValid: false, error: 'NPI must contain only digits' };
  }

  // Check if exactly 10 digits
  if (cleanNPI.length !== 10) {
    return { isValid: false, error: 'NPI must be exactly 10 digits' };
  }

  // Check if starts with 1 or 2 (basic NPI format requirement)
  const firstDigit = cleanNPI.charAt(0);
  if (firstDigit !== '1' && firstDigit !== '2') {
    return { isValid: false, error: 'NPI must start with 1 or 2' };
  }

  // Frontend only does basic format validation
  // Backend will handle Luhn checksum validation
  return { isValid: true };
}

export const NPIEntryStep: React.FC<NPIEntryStepProps> = ({
  currentNPI,
  onNext,
  onBack,
  onNPIUpdate,
  isLoading
}) => {
  const [npiValue, setNpiValue] = useState(currentNPI);
  const [validation, setValidation] = useState<{ isValid: boolean; error?: string }>({ isValid: false });
  const [hasInteracted, setHasInteracted] = useState(false);

  // Validate NPI whenever value changes
  useEffect(() => {
    if (npiValue) {
      const result = validateNPI(npiValue);
      setValidation(result);
    } else {
      setValidation({ isValid: false });
    }
  }, [npiValue]);

  // Handle input change with formatting
  const handleNPIChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numeric input, max 10 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 10);
    setNpiValue(numericValue);
    setHasInteracted(true);
    onNPIUpdate(numericValue);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validation.isValid && !isLoading) {
      onNext();
    }
  };

  // Handle Enter key - let form submission handle it naturally
  const handleKeyDown = (_e: React.KeyboardEvent) => {
    // Enter key will trigger form submission automatically
    // No need to handle it separately to avoid double calls
  };

  const showValidationSuccess = hasInteracted && validation.isValid;
  const showValidationError = hasInteracted && !validation.isValid && validation.error;

  return (
    <div className="max-w-2xl mx-auto p-6">
      <form role="form" onSubmit={handleSubmit} className="space-y-6">
        {/* Step Header */}
        <div className="text-center">
          <h2 aria-level={2} className="text-2xl font-semibold text-gray-900 mb-2">
            NPI Number Confirmation
          </h2>
          <p className="text-gray-600">
            Confirm your National Provider Identifier (NPI) to proceed with verification
          </p>
        </div>

        {/* NPI Input Field */}
        <div className="space-y-2">
          <label htmlFor="npi-input" className="block text-sm font-medium text-gray-700">
            NPI Number
          </label>
          
          <div className="relative">
            <input
              id="npi-input"
              type="text"
              value={npiValue}
              onChange={handleNPIChange}
              onKeyDown={handleKeyDown}
              maxLength={10}
              className={`
                w-full px-4 py-3 text-lg border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                ${isLoading ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
                ${showValidationSuccess ? 'border-green-500' : ''}
                ${showValidationError ? 'border-red-500' : 'border-gray-300'}
              `}
              placeholder="Enter your 10-digit NPI"
              disabled={isLoading}
              aria-describedby="npi-help npi-validation-feedback"
            />
            
            {/* Validation Icons */}
            {isLoading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Loader2 data-testid="npi-loading-spinner" className="h-5 w-5 animate-spin text-blue-500" />
              </div>
            )}
            
            {showValidationSuccess && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <CheckCircle data-testid="npi-valid-icon" className="h-5 w-5 text-green-500" />
              </div>
            )}
            
            {showValidationError && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <X className="h-5 w-5 text-red-500" />
              </div>
            )}
          </div>

          {/* Help Text */}
          <p id="npi-help" className="text-sm text-gray-500">
            Your NPI is a unique 10-digit identification number for healthcare providers
            {currentNPI && (
              <span className="ml-2 text-blue-600 font-medium">
                (from invitation)
              </span>
            )}
          </p>

          {/* Validation Feedback */}
          <div id="npi-validation-feedback" data-testid="npi-validation-feedback" className="min-h-[1.5rem]">
            {showValidationSuccess && (
              <div data-testid="npi-validation-success" className="flex items-center text-sm text-green-600">
                <CheckCircle className="h-4 w-4 mr-1" />
                Valid NPI number
              </div>
            )}
            
            {showValidationError && (
              <div className="text-sm text-red-600">
                <X className="h-4 w-4 inline mr-1" />
                {validation.error}
              </div>
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className={`
              px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500
              ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            Back
          </button>

          <button
            type="submit"
            disabled={!validation.isValid || isLoading}
            className={`
              px-6 py-2 rounded-lg text-white font-medium focus:ring-2 focus:ring-blue-500
              ${validation.isValid && !isLoading 
                ? 'bg-blue-600 hover:bg-blue-700' 
                : 'bg-gray-400 cursor-not-allowed'
              }
            `}
          >
            {isLoading ? (
              <span className="flex items-center">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Verifying...
              </span>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}; 